// ELO calculation constants
export const K_FACTOR = 32; // Standard K-factor for chess-like games
export const INITIAL_ELO = 1200;

// Helper function to calculate expected score
export const calculateExpectedScore = (ratingA: number, ratingB: number): number => {
    return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
};

// Helper function to calculate new ELO rating
export const calculateNewElo = (oldRating: number, opponentRating: number, actualScore: number): number => {
    const expectedScore = calculateExpectedScore(oldRating, opponentRating);
    return Math.round(oldRating + K_FACTOR * (actualScore - expectedScore));
};

// Helper function to determine actual score from match results
export const getActualScore = (teamScore: number, opponentScore: number): number => {
    if (teamScore > opponentScore) return 1; // Win
    if (teamScore < opponentScore) return 0; // Loss
    return 0.5; // Draw
};

// Interface for ELO rating data
export interface EloRating {
    teamId: string;
    coachName: string;
    divisionName: string;
    currentElo: number;
    eloHistory: { week: string; elo: number; change?: number }[];
    rank?: number;
}

// Interface for team data
export interface TeamData {
    teamId: string;
    coachName: string;
    divisionName: string;
}

// Interface for match data
export interface MatchData {
    homeTeamId: string;
    awayTeamId: string;
    results?: {
        homeTeamScore: number;
        awayTeamScore: number;
    };
}

// Interface for week data
export interface WeekData {
    weekName: string;
    matches: MatchData[];
}

// Interface for division data
export interface DivisionData {
    divisionName: string;
    teams: { teamId: string; teamName: string; coachId: string; race: string }[];
}

// Calculate ELO ratings for all teams
export const calculateEloRatings = (
    divisions: DivisionData[],
    weeks: WeekData[],
    leagueCoaches: { coachId: string; coachName: string }[]
): EloRating[] => {
    // Get all available teams for ELO calculation
    const getAllTeams = (): TeamData[] => {
        const teams: TeamData[] = [];
        divisions.forEach(division => {
            division.teams.forEach(team => {
                const coach = leagueCoaches.find(c => c.coachId === team.coachId);
                teams.push({
                    teamId: team.teamId,
                    coachName: `${team.teamName} - ${coach ? coach.coachName : `Coach ${team.coachId}`}`,
                    divisionName: division.divisionName
                });
            });
        });
        return teams;
    };

    const teams = getAllTeams();
    const eloRatings: Record<string, EloRating> = {};

    // Initialize all teams with starting ELO
    teams.forEach(team => {
        eloRatings[team.teamId] = {
            teamId: team.teamId,
            coachName: team.coachName,
            divisionName: team.divisionName,
            currentElo: INITIAL_ELO,
            eloHistory: [{ week: 'Start', elo: INITIAL_ELO }]
        };
    });

    // Process each week's matches to update ELO ratings (exclude preseason so game 1 starts at initial ELO)
    const isPreseasonWeek = (weekName: string) =>
        weekName.toLowerCase().includes('preseason');
    weeks.forEach(week => {
        if (isPreseasonWeek(week.weekName)) return;
        week.matches.forEach(match => {
            if (match.results) {
                const homeTeam = eloRatings[match.homeTeamId];
                const awayTeam = eloRatings[match.awayTeamId];

                if (homeTeam && awayTeam) {
                    // Calculate scores
                    const homeScore = getActualScore(match.results.homeTeamScore, match.results.awayTeamScore);
                    const awayScore = getActualScore(match.results.awayTeamScore, match.results.homeTeamScore);

                    // Calculate new ELO ratings
                    const newHomeElo = calculateNewElo(homeTeam.currentElo, awayTeam.currentElo, homeScore);
                    const newAwayElo = calculateNewElo(awayTeam.currentElo, homeTeam.currentElo, awayScore);

                    // Calculate changes
                    const homeChange = newHomeElo - homeTeam.currentElo;
                    const awayChange = newAwayElo - awayTeam.currentElo;

                    // Update ELO ratings
                    homeTeam.currentElo = newHomeElo;
                    awayTeam.currentElo = newAwayElo;

                    // Add to history
                    homeTeam.eloHistory.push({
                        week: week.weekName,
                        elo: newHomeElo,
                        change: homeChange
                    });
                    awayTeam.eloHistory.push({
                        week: week.weekName,
                        elo: newAwayElo,
                        change: awayChange
                    });
                }
            }
        });
    });

    const ratings = Object.values(eloRatings);

    // Sort all ratings by current ELO (highest first) and add overall ranks
    ratings.sort((a, b) => b.currentElo - a.currentElo);
    ratings.forEach((rating, index) => {
        rating.rank = index + 1;
    });

    return ratings;
}; 